import {
  Button,
  Center,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Stack,
  StackDivider,
} from '@chakra-ui/react';
import { get, noop } from 'lodash';
import type { FC } from 'react';
import { type UseFormWatch, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from 'urql';

import { isRoleAdmin, useGetLoggedInUser } from '@revelio/auth';
import { Loading } from '@revelio/core';
import { type Client, type InputMaybe, Role } from '@revelio/data-access';

import { FieldGroup } from '../../../shared/ui/admin/groups/field/field-group';
import ClientForm, { type ClientFormValues } from '../client-form';
import UserForm, { type UserFormValues } from '../user-form';
import { ClientDetailsQuery } from '../userOperations';
import { USER_ROLE_OPTION, useSubmitUser } from '../utils/helpers';

interface AdminUserAddProps {
  isOpen?: boolean;
  onClose?: () => void;
}

const AdminUserAdd: FC<AdminUserAddProps> = ({ isOpen, onClose }) => {
  const {
    loggedInUser,
    query: [{ fetching: loggedInUserLoading }],
  } = useGetLoggedInUser();
  const isAdmin = isRoleAdmin(loggedInUser.role);
  const isClientAdmin = loggedInUser.role === Role.ClientAdmin;

  const userFormMethods = useForm<UserFormValues>({
    defaultValues: { role: USER_ROLE_OPTION },
  });
  const clientReadonlyForm = useForm<ClientFormValues>();

  const [searchParams] = useSearchParams();
  const clientNameParam = searchParams.get('client');
  const newUserClient = userFormMethods.watch('client_group');

  const clientQueryId = newUserClient?.value
    ? newUserClient?.value
    : clientNameParam || undefined;

  const [{ data: clientData }] = useQuery({
    query: ClientDetailsQuery,
    variables: { ids: [clientQueryId as InputMaybe<string>] },
    pause: !clientQueryId,
  });

  const clientDetails: Client = get(clientData, 'clients[0]');

  const { addUser, addUserLoading } = useSubmitUser({
    loggedInUser,
    isClientAdmin,
    onClose,
  });

  const submitAddUser = userFormMethods.handleSubmit((formData) => {
    addUser({ formData, specifiedClient: clientDetails });
  });

  return (
    <Modal
      isOpen={isOpen || false}
      onClose={onClose || noop}
      size="5xl"
      isCentered
      trapFocus={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add a new user</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {!loggedInUserLoading ? (
            <form onSubmit={submitAddUser}>
              <Stack divider={<StackDivider />} spacing="6">
                <Stack direction="row" divider={<StackDivider />} spacing="6">
                  <div style={{ width: '930px' }}>
                    <FieldGroup
                      title="Add User"
                      description="Create a new account user"
                    >
                      <UserForm
                        useFormMethods={userFormMethods}
                        userToEdit={null}
                        defaultClient={clientDetails}
                        setPassword={(newPassword: string) =>
                          userFormMethods.setValue('password', newPassword)
                        }
                      />
                    </FieldGroup>
                  </div>

                  {!isClientAdmin && (
                    <ClientForm
                      clientToEdit={clientDetails}
                      readonly={true}
                      title={
                        clientDetails
                          ? `${clientDetails?.client_name} settings`
                          : 'User client group'
                      }
                      description={
                        clientDetails
                          ? "To edit client group, go to edit one of client's user"
                          : 'Choose client group to see settings'
                      }
                      register={clientReadonlyForm.register}
                      control={clientReadonlyForm.control}
                      watch={
                        clientReadonlyForm.watch as unknown as UseFormWatch<ClientFormValues>
                      }
                    />
                  )}
                </Stack>

                <FieldGroup>
                  <Stack direction="row" spacing={4} align="center">
                    <Button
                      colorScheme="navyBlue"
                      variant="solid"
                      size="md"
                      onClick={onClose}
                    >
                      Cancel
                    </Button>
                    {isAdmin && (
                      <Button
                        colorScheme="green"
                        variant="solid"
                        size="md"
                        isLoading={addUserLoading}
                        type="submit"
                      >
                        Add User
                      </Button>
                    )}
                    <Spacer />
                  </Stack>
                </FieldGroup>
              </Stack>
            </form>
          ) : (
            <Flex justify="center" minHeight="200px">
              <Center>
                <Loading />
              </Center>
            </Flex>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AdminUserAdd;
